import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import styles from './Receipt.module.scss';
import { Payment } from '@spike/payments-model';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper, usePermissions } from 'hooks';
import { RootState } from 'store';
import {
    PaymentsStatus,
    sendThunk,
    downloadReceiptPDFThunk,
    printPaymentThunk,
    sendReceiptThunk
} from '@spike/payments-action';
import useNonInitialEffect from '@versiondos/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Refund } from 'components/Payments';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import Button from 'components/UI/V3/Button/Button';
import { faClose, faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { faArrowToBottom, faPaperPlane } from '@fortawesome/pro-light-svg-icons';
import { faUndoAlt, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faPrint } from '@fortawesome/pro-light-svg-icons';
import clsx from 'clsx';
import ToolbarButton from '../Toolbar/ToolbarButton';
import ToolbarItem from '../Toolbar/ToolbarItem';
import { useMediaQuery } from 'react-responsive';
import { Box, Modal } from '@material-ui/core';
import SendReceiptDialog from 'components/UI/SendReceiptDialog';
import ControlledTextField2 from '../ControlledTextField2';
import { FieldError } from '@spike/model';
import { isEmpty } from 'lodash';
import { isEmailValid } from '@spike/validations';
import { getPaymentMethodLabel, isCardPayment } from 'utils/PaymentsUtils';
import getCardIcon from 'hooks/useCardIcon';

interface ReceiptProps {
    payment: Payment;
    withClose?: boolean;
    refundModalZIndex?: number;
    onBack?: () => void;
    onRefunded?: () => void;
}

export const Receipt: FunctionComponent<ReceiptProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const isMobile = useMediaQuery({ query: '(max-width: 959.95px)' });

    const permissions = usePermissions();
    const allowRefunds = permissions.checkout.refunds;

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const [showRefund, setShowRefund] = useState(false);
    const [sendingReceipt, setSendingReceipt] = useState(false);
    const [showMobileDropdown, setShowMobileDropdown] = useState(false);
    const [sendShowReceipt, setSendShowReceipt] = useState(false);
    const [email, setEmail] = useState<string>(
        (props.payment.customer.email ?? '').includes('temporal.cuddlesapp.com')
            ? ''
            : props.payment.customer.email ?? ''
    );
    const [errors, setErrors] = useState<Array<FieldError>>([]);

    const hasSurcharge = useMemo(() => Number(props.payment.surchargeAmount) > 0, [props.payment.surchargeAmount]);
    const paymentMethodLabel = useMemo(() => getPaymentMethodLabel(props.payment), [props.payment]);
    const cardIcon = useMemo(
        () => (isCardPayment(props.payment) ? getCardIcon(props.payment.paymentMethod?.id) : undefined),
        [props.payment]
    );

    useNonInitialEffect(() => {
        if (PaymentsStatus.SendReceiptSuccess === paymentsStatus) {
            setSendingReceipt(false);
            setSendShowReceipt(false);
        }
    }, [paymentsStatus]);

    useEffect(() => {
        const errors = validateEmail();
        setErrors(errors);
    }, [email]);

    const sendingReceipHandler = () => {
        setShowMobileDropdown(false);
        setSendingReceipt(true);
        dispatch(sendReceiptThunk(apiClientWrapper, props.payment.id, email));
    };

    const handlePrintReceipt = () => {
        setShowMobileDropdown(false);

        const link = document.createElement('a');
        link.href = props.payment.receiptPdfUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadReceipt = () => {
        setShowMobileDropdown(false);

        dispatch(
            downloadReceiptPDFThunk(
                apiClientWrapper,
                props.payment.id,
                props.payment.receiptNumber,
                props.payment.customer.name,
                props.payment.createdAt?.format('MM/DD/YYYY, h:mm a')
            )
        );
    };

    const validateEmail = (): Array<FieldError> => {
        const errors: Array<FieldError> = [];

        if (!email || isEmpty(email.trim())) {
            errors.push({
                fieldName: 'email',
                errorMessage: 'Email is required'
            });
        }

        if (email && email?.trim()?.length > 0 && !isEmailValid(email || '')) {
            errors.push({
                fieldName: 'email',
                errorMessage: 'Email incorrect format'
            });
        }

        return errors;
    };

    return (
        <div className={styles.receiptContainer}>
            <div className={styles.topMenu}>
                {isMobile ? (
                    <FontAwesomeIcon
                        icon={faEllipsis}
                        className={styles.ellipsisIcon}
                        onClick={() => setShowMobileDropdown(prev => !prev)}
                    />
                ) : (
                    <ToolbarButton>
                        <ToolbarItem
                            text="Print Receipt"
                            icon={faPrint}
                            onClick={handlePrintReceipt}
                        />
                        <ToolbarItem
                            text="Download"
                            icon={faArrowToBottom}
                            onClick={handleDownloadReceipt}
                        />
                    </ToolbarButton>
                )}
                {(isMobile || props.withClose) && props.onBack && (
                    <FontAwesomeIcon
                        icon={faClose}
                        size="xl"
                        onClick={props.onBack}
                    />
                )}
            </div>

            <div className={styles.receipt}>
                <div className={styles.receiptHeader}>
                    <div className={styles.receiptNumber}>Receipt #{props.payment.receiptNumber}</div>
                    <div className={styles.authorizationBadge}>
                        <FontAwesomeIcon icon={faCheck} />
                        <span>{props.payment.status.name}</span>
                    </div>
                </div>

                <div className={styles.receiptDetails}>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Client</span>
                        <span className={styles.detailValue}>{props.payment.customer.name}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Date</span>
                        <span className={styles.detailValue}>
                            {props.payment.createdAt?.format('MM/DD/YYYY, h:mm a')}
                        </span>
                    </div>
                    <div className={clsx(styles.detailRow, styles.divider)}>
                        <span className={styles.detailLabelTotal}>Total</span>
                        <span className={styles.detailValueTotal}>${Number(props.payment.baseAmount).toFixed(2)}</span>
                    </div>
                    {hasSurcharge && (
                        <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>Surcharge</span>
                            <span className={styles.detailValue}>${props.payment.surchargeAmount}</span>
                        </div>
                    )}
                    <div className={styles.detailRow}>
                        <span
                            className={clsx(
                                hasSurcharge ? styles.detailLabelTotal : styles.detailLabel,
                                styles.paymentMethod
                            )}
                        >
                            {cardIcon && (
                                <img
                                    src={cardIcon}
                                    alt=""
                                    className={styles.cardIcon}
                                />
                            )}
                            {paymentMethodLabel}
                        </span>
                        <span className={hasSurcharge ? styles.detailValueTotal : styles.detailValue}>
                            ${Number(props.payment.amount).toFixed(2)}
                        </span>
                    </div>
                </div>

                <div className={styles.actionButtons}>
                    <div>
                        <Button
                            label="View Receipt"
                            variant="black"
                            fullWidth={true}
                            onClick={handlePrintReceipt}
                        />
                    </div>
                    <div className={styles.secondaryButtons}>
                        {props.payment.refundable && allowRefunds && (
                            <div
                                className={styles.secondaryButton}
                                onClick={() => setShowRefund(true)}
                            >
                                <FontAwesomeIcon icon={faUndoAlt} />
                                <span className={styles.secondaryButtonText}>Refund</span>
                            </div>
                        )}
                        <div
                            className={styles.secondaryButton}
                            onClick={() => setSendShowReceipt(true)}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                            <span className={styles.secondaryButtonText}>Send Receipt</span>
                        </div>
                    </div>
                </div>
            </div>

            {showRefund && (
                <Modal
                    open={true}
                    aria-labelledby="simple-dialog-title"
                    className={styles.modal}
                    style={props.refundModalZIndex !== undefined ? { zIndex: props.refundModalZIndex } : undefined}
                >
                    <Refund
                        paymentId={props.payment.id}
                        onClose={() => setShowRefund(false)}
                        onRefunded={() => {
                            setShowRefund(false);
                            props.onRefunded?.();
                        }}
                    />
                </Modal>
            )}

            {showMobileDropdown && (
                <ActionsPopup
                    actions={[
                        {
                            icon: faPrint,
                            label: 'Print Receipt',
                            onClick: handlePrintReceipt
                        },
                        {
                            icon: faArrowToBottom,
                            label: 'Download',
                            onClick: handleDownloadReceipt
                        }
                    ]}
                    onClose={() => setShowMobileDropdown(false)}
                />
            )}

            {sendShowReceipt && (
                <SendReceiptDialog
                    open={true}
                    isSuccess={true}
                    title={<p className={styles.titleDialog}>Send Receipt</p>}
                    question={
                        <Box className={styles.emailContainer}>
                            <ControlledTextField2
                                label="Email"
                                placeholder="Email Address"
                                name="email"
                                value={email}
                                onChange={setEmail}
                                autoComplete={'off'}
                                errors={errors}
                            />
                        </Box>
                    }
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Send"
                    processing={sendingReceipt}
                    disabled={errors.length > 0}
                    onCancel={() => setSendShowReceipt(false)}
                    onConfirm={() => {
                        if (errors.length === 0) {
                            sendingReceipHandler();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default Receipt;
